import { useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { ShieldCheck, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

type AllowedEmail = {
  id: number
  email: string
  note: string | null
  has_account: boolean
  created_at: string
}

export default function AdminAllowedEmailsIndex({
  allowed_emails,
}: {
  allowed_emails: AllowedEmail[]
}) {
  const errors = (usePage().props.errors ?? {}) as Record<string, string>
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")

  function add(e: React.FormEvent) {
    e.preventDefault()
    router.post(
      "/admin/allowed_emails",
      { email, note },
      { preserveScroll: true, onSuccess: () => { setEmail(""); setNote("") } },
    )
  }

  function remove(item: AllowedEmail) {
    if (
      window.confirm(
        `Remove ${item.email} from the allowlist? They will be signed out and unable to log in.`,
      )
    ) {
      router.delete(`/admin/allowed_emails/${item.id}`, { preserveScroll: true })
    }
  }

  return (
    <>
      <Head title="Access">
        <meta name="description" content="Control who may register or log in to SONAR." />
        <meta property="og:title" content="Access" />
        <meta property="og:description" content="Control who may register or log in to SONAR." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Access</h1>
          <p className="mt-1">
            Only these email addresses may register or log in. Everyone else is blocked. Removing an
            address signs that person out immediately.
          </p>
        </div>

        <form onSubmit={add} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex-1">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              aria-label="Email to allow"
              required
            />
            {errors.email && <p className="mt-1 text-sm text-danger">{errors.email}</p>}
          </div>
          <div className="sm:w-48">
            <Input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              aria-label="Note"
            />
          </div>
          <Button type="submit">Add</Button>
        </form>

        <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
          {allowed_emails.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-4 py-3">
              <ShieldCheck className="h-4 w-4 shrink-0 text-ink-muted" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink-display">{item.email}</div>
                <div className="truncate text-xs text-ink-muted">
                  {item.note ? `${item.note} · ` : ""}Added {formatDate(item.created_at)}
                </div>
              </div>
              {item.has_account ? (
                <Badge tone="accent">Has account</Badge>
              ) : (
                <Badge>Invited</Badge>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(item)}
                aria-label={`Remove ${item.email}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
        {allowed_emails.length === 0 && (
          <p className="mt-4 text-sm text-ink-muted">No addresses yet — add one above.</p>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
